import { Method } from 'axios';
import { Executable } from './executable';

/**
 * The AssociateTokenInput defines the expected input for the SDK Request to associate a token to a user account.
 */
export interface AssociateTokenInput {
  /**
   * Destination account (typically an email address).
   */
  destinationAccount: string;
  /**
   * The quantity of tokens.  If BambuMeta is maintaining the account, the number can be positive or negative, if not only positive
   */
  quantity: number;
  /**
   * The serviceType defining the Loyalty Token for the tenant.  eg. "LoyaltyToken"
   */
  serviceType: string;
}

/**
 * The expected response object from the AssociateToken request.
 */
export interface AssociateTokenResponse {
  /**
   * A response message returned by the API.
   */
  message: string;
}

/**
 * Associate token takes a destinationAccount identifier (typically an email address), a quantity of tokens to associate and a serviceType.  This information is used to invoke the associate-token api to add the quantity of tokens defined in the service type to the destination account, assuming the account exists.
 */
export class AssociateToken extends Executable<AssociateTokenResponse> {
  private _destinationAccount?: string;

  private _quantity?: number;

  private _serviceType?: string;

  /**
   * Instantiates the AssociateToken request.
   * @param input <AssociateTokenInput>
   */
  constructor(input?: AssociateTokenInput) {
    super();
    if (input) {
      this._destinationAccount = input.destinationAccount;
      this._quantity = input.quantity;
      this._serviceType = input.serviceType;
    }
  }

  /**
   * Sets the destination account on the request and returns the object.
   * @param value <string> Destination account (typically an email address).
   * @returns <AssociateToken>
   */
  public setDestinationAccount(value: string): AssociateToken {
    this._destinationAccount = value;
    return this;
  }

  /**
   * Sets the quantity of tokens to transfer.
   * @param value The quantity of tokens.  If BambuMeta is maintaining the account, the number can be positive or negative, if not only positive
   * @returns <AssociateToken>
   */
  public setQuantity(value: number): AssociateToken {
    this._quantity = value;
    return this;
  }

  /**
   * Sets the serviceType of the request.
   * @param value The serviceType defining the Loyalty Token for the tenant.  eg. "LoyaltyToken"
   * @returns <AssociateToken>
   */
  public setServiceType(value: string): AssociateToken {
    this._serviceType = value;
    return this;
  }

  /**
   * getBody is used by the BambuClient to properly construct the request object body.
   * @returns Returns the expected body of the request utilizing the defined request attributes.
   */
  getBody() {
    const input: any = {};
    if (this._destinationAccount) {
      input.destinationAccount = this._destinationAccount;
    }
    if (this._quantity) {
      input.quantity = this._quantity;
    }
    if (this._serviceType) {
      input.serviceType = this._serviceType;
    }
    return input;
  }

  /**
   * getMethod is used by the BambuClient to properly construct the request call.
   * @returns request method
   */
  public getMethod(): Method {
    return 'post';
  }

  /**
   * getResponse is used by the BambuClient to propery return the response object type.
   * @param apiResponse the api response object returned by the BambuMeta client before being converted to the AssociateTokenResponse
   * @returns AssociateTokenResponse
   */
  public getResponse(apiResponse: any): AssociateTokenResponse {
    return apiResponse;
  }

  /**
   * getURLPath is used by the BambuClient to properly construct the request call.
   * @param tenantId the tenant id defined in the BambuClient
   * @returns request url
   */
  public getURLPath(tenantId?: string): string {
    return `${tenantId}/associate-token`;
  }

  /**
   * getRootPath is used by the BambuClient to properly construct the request call.
   * @returns The root path of the request
   */
  getRootPath(): string | undefined {
    return undefined;
  }
}
